import React, { useState, useEffect, useRef, useCallback } from "react";

import { Form, Formik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";

import {
  SimpleGrid,
  Grid,
  GridItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Flex,
  Text,
  Icon,
  HStack,
  Tooltip,
  Box,
  useBreakpointValue,
} from "@chakra-ui/react";
import { IoHelpCircle } from "react-icons/io5";

import CustomDropzone from "../../../../../components/CustomDropzone";
import SituacaoJuridicaService from "../../../../../../main/services/SituacaoJuridicaService";
import CriancaService from "../../../../../../main/services/CriancaService";
import { CustomButton } from "../../../../../components/CustomButton";
import { CustomSelectWField } from "../../../../../components/CustomSelectWField";
import { FocusError } from "../../../../../components/FocusError";

import { CustomInputWField } from "../../../../../components/CustomInputWField";
import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";

import { hasFormChanged } from "../../../../../utils/hasFormChanged";
import { useAppContext } from "../../../../../../Context/MessageContextProvider";
import ChangeRadio from "../../../../../components/ChangeRadio";
import { numeroProcesso } from "../../../../../utils/masks";
import { SituacaoJuridicaVisualizarModal } from "../Visualizar/SituacaoJuridicaVisualizarModal";
import { CustomTextArea } from "../../../../../components/CustomTextArea";
import { deleteUrlParam } from "../../../../../utils/deleteUrlParam";
import { useHistory, useLocation } from "react-router-dom";
import { PopoverGenero } from "../../../../../components/PopoverGenero";
import { servicoAcolhimentoFunc } from "../../../../../../helpers/Funcionalidades";

const suportedFormats = [
  ".png",
  ".bmp",
  ".tiff",
  ".jpg",
  ".jpeg",
  ".doc",
  ".docx",
  ".txt",
  ".dot",
  ".pdf",
  ".xml",
  ".xls",
  ".xlam",
  ".ods",
];

export const SituacaoJuridicaEditar = ({
  isOpen,
  onOpen,
  onClose,
  filiacoes,
  fetchProcessosProcedimentoData,
  selectedFiliacao,
  updateFromVisualizacao,
  isVisualizacao,
  fetchVisitasData,
  onCloseFiliacoes,
  form,
  initialFiliacao,
  roleKeycloack,
  idCrianca,
  fetchAtribuicao,
}) => {
  const [isVisual, setIsVisual] = useState(!!isVisualizacao);
  const [servicosData, setServicosData] = useState({
    options: [],
    isLoading: false,
  });
  const [, setSelectedCrianca] = useState(null);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const {
    cadastrarProcessosProcedimentos,
    setCadastrarProcessosProcedimentos,
  } = useAppContext();
  const [irmaosData, setIrmaosData] = useState({
    options: [],
    isLoading: false,
  });

  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idTipoPendencia = searchParams.get("idTipoPendencia");
  const idAnexo = searchParams.get("idAnexo");
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [hintCompartilhar, setHintCompartilhar] = useState(false);

  const schema = Yup.object().shape({
    numeroProcesso: Yup.string()
      .required("Este campo é obrigatório!")
      .test("is-valid-numerics", "Número inválido", function (value) {
        if (value) {
          let temp = value.replace(/\D/g, "").slice(0, 20);

          const numericCount = temp.length;

          return numericCount >= 20;
        }

        return true;
      }),
    liminarFamiliaSubstituta: Yup.string().required(
      "Este campo é obrigatório!"
    ),
    sentenca: Yup.string()
      .required("Este campo é obrigatório!")
      .test(
        "sentenca-requerida",
        "Por favor, marque a opção sim para sentença e selecione o tipo correto",
        function (value) {
          const { transitoJulgado } = this.parent;
          if (transitoJulgado === "S" && (value === "N" || value === "A")) {
            return false;
          }
          return true;
        }
      ),
    recurso: Yup.string().required("Este campo é obrigatório!"),
    transitoJulgado: Yup.string().required("Este campo é obrigatório!"),
    compartilharProcesso: Yup.string().test(
      "required-if-irmaos",
      "Este campo é obrigatório!",
      function (value) {
        if (
          irmaosData &&
          irmaosData.options &&
          irmaosData.options.length > 0 &&
          !(
            roleKeycloack &&
            roleKeycloack.includes(servicoAcolhimentoFunc.visualizarParteTJ)
          )
        ) {
          return !!value;
        }
        return true;
      }
    ),

    irmaos: Yup.array()
      .nullable()
      .test("irmaosArrayLength", "Este campo é obrigatório!", function (value) {
        const { compartilharProcesso } = this.parent;

        const irmaosCompartilhados = value
          .filter((irmao) => irmao.compartilhado === "S")
          .map((irmao) => irmao);

        if (irmaosCompartilhados.length === 0 && compartilharProcesso === "S") {
          return false;
        }

        return true;
      }),
    idSituacaoJuridica: Yup.object()
      .nullable()
      .required("Este campo é obrigatório!"),
    idTipoSentenca: Yup.object().when("sentenca", {
      is: (sentenca) => sentenca === "S",
      then: Yup.object().nullable().required("Este campo é obrigatório"),
      otherwise: Yup.object().nullable(),
    }),
    informacaoComplementar: Yup.string(),
    anexoPeticaoInicial: Yup.mixed()
      .nullable()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (!value) {
          return true;
        } else {
          return value?.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value && value.path) {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        } else {
          return true;
        }
      }),
    anexoDecisaoLiminar: Yup.mixed()
      .nullable()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (!value) {
          return true;
        } else {
          return value?.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value && value.path) {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        } else {
          return true;
        }
      }),
    anexoSentenca: Yup.mixed()
      .nullable()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (!value) {
          return true;
        } else {
          return value?.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value && value.path) {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        } else {
          return true;
        }
      }),
    anexoPecaRecursal: Yup.mixed()
      .nullable()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (!value) {
          return true;
        } else {
          return value?.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value && value.path) {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        } else {
          return true;
        }
      }),
    anexoCertidaoTransitoJulgado: Yup.mixed()
      .nullable()
      .test("fileSize", "O arquivo tem mais de 10MB.", (value) => {
        if (!value) {
          return true;
        } else {
          return value?.size <= 10000000;
        }
      })
      .test("fileType", "Anexar uma extensão válida.", (value) => {
        if (value && value.path) {
          let haveFormat = false;
          suportedFormats.forEach((format) => {
            if (value.path.toString().toLowerCase().includes(format)) {
              haveFormat = true;
            }
          });
          return haveFormat;
        } else {
          return true;
        }
      }),
  });

  useEffect(() => {
    if (idCrianca) {
      const handleGetIrmaosProcessoProcedimento = async () => {
        setIrmaosData((prevIrmaosData) => ({
          ...prevIrmaosData,
          isLoading: true,
        }));
        await CriancaService.getIrmaosProcessoProcedimento(idCrianca).then(
          (res) => {
            setIrmaosData({ options: res.data, isLoading: false });
          }
        );
      };
      handleGetIrmaosProcessoProcedimento();
    }
  }, [idCrianca]);

  const [initialValues, setInitialValues] = useState({
    numeroProcesso: "",
    anexoPeticaoInicial: null,
    liminarFamiliaSubstituta: "",
    sentenca: "",
    recurso: "",
    transitoJulgado: "",
    anexoDecisaoLiminar: null,
    anexoSentenca: null,
    anexoPecaRecursal: null,
    anexoCertidaoTransitoJulgado: null,
    informacaoComplementar: "",
    compartilharProcesso: "",
    irmaos: [],
    idSituacaoJuridica: "",
    idTipoSentenca: "",
    irmaosData: irmaosData.options,
  });

  const [anexoPeticaoInicialEditado, setAnexoPeticaoInicialEditado] =
    useState(false);
  const [anexoDecisaoLiminarEditado, setAnexoDecisaoLiminarEditado] =
    useState(false);
  const [anexoSentencaEditado, setAnexoSentencaEditado] = useState(false);
  const [anexoPecaRecursalEditado, setAnexoPecaRecursalEditado] =
    useState(false);
  const [
    anexoCertidaoTransitoJulgadoEditado,
    setAnexoCertidaoTransitoJulgadoEditado,
  ] = useState(false);

  const backAlert = () => {
    if (cadastrarProcessosProcedimentos) {
      Swal.fire({
        title: "Tem certeza que deseja cancelar?",
        text: "As informações serão perdidas.",
        icon: "error",
        showCancelButton: true,
        confirmButtonColor: "#169499",
        cancelButtonColor: "#169499",
        confirmButtonText: "Sim",
        cancelButtonText: "Não",
      }).then((result) => {
        if (result.isConfirmed) {
          onClose();
          setCadastrarProcessosProcedimentos(false);
        }
      });
    } else {
      onClose();
    }
  };

  useEffect(() => {
    const {
      numeroProcesso,
      liminarFamiliaSubstituta,
      sentenca,
      recurso,
      transitoJulgado,
      anexoDecisaoLiminar,
      anexoSentenca,
      anexoPecaRecursal,
      anexoCertidaoTransitoJulgado,
      informacaoComplementar,
      compartilharProcesso,
      irmaos,
      idTipoSentenca,
      idSituacaoJuridica,
      descricaoTipoSentenca,
      descricaoSituacaoJuridica,
      anexoPeticaoInicial,
      ...resto
    } = selectedFiliacao;

    const createFakeFile = (anexo) => {
      if (anexo) {
        const blobData = new Blob([], { type: `${anexo.mimetype}` });
        return new File([blobData], anexo.nomeArquivo, {
          type: `${anexo.mimetype}`,
        });
      }
      return null;
    };

    setInitialValues({
      numeroProcesso: numeroProcesso ? numeroProcesso : "",
      liminarFamiliaSubstituta: liminarFamiliaSubstituta
        ? liminarFamiliaSubstituta
        : "",
      sentenca: sentenca ? sentenca : "",
      recurso: recurso ? recurso : "",
      transitoJulgado: transitoJulgado ? transitoJulgado : "",
      anexoPeticaoInicial: anexoPeticaoInicial
        ? createFakeFile(anexoPeticaoInicial)
        : "",
      anexoDecisaoLiminar: anexoDecisaoLiminar
        ? createFakeFile(anexoDecisaoLiminar)
        : "",
      anexoSentenca: anexoSentenca ? createFakeFile(anexoSentenca) : "",
      anexoPecaRecursal: anexoPecaRecursal
        ? createFakeFile(anexoPecaRecursal)
        : "",
      anexoCertidaoTransitoJulgado: anexoCertidaoTransitoJulgado
        ? createFakeFile(anexoCertidaoTransitoJulgado)
        : "",
      informacaoComplementar: informacaoComplementar
        ? informacaoComplementar
        : "",
      compartilharProcesso:
        compartilharProcesso === "S"
          ? "S"
          : compartilharProcesso === "N"
          ? "N"
          : "",
      irmaos: irmaos,
      idTipoSentenca: idTipoSentenca
        ? { id: idTipoSentenca, descricao: descricaoTipoSentenca }
        : "",
      idSituacaoJuridica: idSituacaoJuridica
        ? {
            id: idSituacaoJuridica,
            label: (
              <HStack justifyContent="space-between" alignItems="center">
                <Text
                  color="#1A203D"
                  fontWeight={400}
                  fontSize={{ base: "12px", md: "14px" }}
                  isTruncated
                >
                  {descricaoSituacaoJuridica}
                </Text>
                <Tooltip
                  label={descricaoSituacaoJuridica}
                  fontSize="md"
                  textAlign={"justify"}
                  placement="top"
                  hasArrow
                >
                  <span className="hint-tooltip">
                    <Icon
                      size={20}
                      aria-label="Aviso de pendente"
                      color="#6B6C71"
                      as={IoHelpCircle}
                      boxSize={{ base: "10px", md: "20px" }}
                    />
                  </span>
                </Tooltip>
              </HStack>
            ),
          }
        : "",
      irmaosData: irmaosData?.options,
      ...resto,
    });
  }, [selectedFiliacao, irmaosData.options]);

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  const handleGetServicos = () => {
    setServicosData({ ...servicosData, isLoading: true });

    SituacaoJuridicaService.pesquisarSituacaoJuridica("", "ativos", "").then(
      (res) => {
        const tempOptions = res.data.map((servicos) => {
          return {
            id: servicos?.id,
            pessoaRestricao: servicos.pessoaRestricao,
            label: (
              <HStack justifyContent="space-between" alignItems="center">
                <Text
                  color="#1A203D"
                  fontWeight={400}
                  fontSize={{ base: "12px", md: "14px" }}
                  isTruncated
                >
                  {servicos.descricao}
                </Text>
                <Tooltip
                  label={servicos.descricao}
                  fontSize="md"
                  textAlign={"justify"}
                  placement="top"
                  hasArrow
                >
                  <span className="hint-tooltip">
                    <Icon
                      size={20}
                      aria-label="Aviso de pendente"
                      color="#6B6C71"
                      as={IoHelpCircle}
                      boxSize={{ base: "10px", md: "20px" }}
                    />
                  </span>
                </Tooltip>
              </HStack>
            ),
          };
        });
        setServicosData({ options: tempOptions, isLoading: false });
      }
    );
  };

  useEffect(() => {
    const handleGetCrianca = async () => {
      await CriancaService.getCrianca(idCrianca).then((res) => {
        setSelectedCrianca(res.data.id);
      });
    };

    handleGetCrianca();
  }, [idCrianca]);

  const editProcessoProcedimento = async (
    id,
    valuesToSend,
    idProcessoProcedimento
  ) => {
    try {
       setLoading(true);
      const res = await CriancaService.putProcessoProcedimento(
        id,
        valuesToSend,
        idProcessoProcedimento
      );
      deleteUrlParam("idAnexo", history);
      (["13", "14", "15", "16", "17"].includes(idAnexo) ||
        ["46", "47"].includes(idTipoPendencia)) &&
        history.replace(location.pathname);
      Swal.fire({
        title: "Sucesso!",
        text: "Registro atualizado com sucesso!",
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#169499",
      }).then((result) => {});
      await CriancaService.getAtribuicaoCompetenciaAtualizarPP(
        id,
        res?.data?.id
      );
      onClose();

     await Promise.all([fetchAtribuicao()]).then(() => {
        fetchProcessosProcedimentoData();
      });
    } catch (error) {
      console.log(error);
      const errorMessage =
        error?.response?.data?.status !== 500 &&
        error?.response?.data?.field !== "processoProcedimentoDuplicado" &&
        error?.response?.data?.field !== "entregaVoluntaria"
          ? JSON.parse(error?.response?.data?.message)
          : "";
      Swal.fire({
        title: "Atenção!",
        html: error?.response?.data?.message
          ? errorMessage?.processoProcedimentoDuplicado ||
            error.response.data.message
          : "",
        icon: "warning",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#169499",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values) => {
    const {
      numeroProcesso,
      liminarFamiliaSubstituta,
      sentenca,
      recurso,
      transitoJulgado,
      anexoDecisaoLiminar,
      anexoSentenca,
      anexoPecaRecursal,
      anexoCertidaoTransitoJulgado,
      informacaoComplementar,
      compartilharProcesso,
      irmaos,
      idSituacaoJuridica,
      idTipoSentenca,
      anexoPeticaoInicial,
    } = values;

    const valuesToSend = {
      idCrianca: idCrianca,
      numeroProcesso: numeroProcesso,
      liminarFamiliaSubstituta: liminarFamiliaSubstituta,
      sentenca: sentenca,
      recurso: recurso,
      transitoJulgado: transitoJulgado,
      anexoPeticaoInicial: anexoPeticaoInicial ? anexoPeticaoInicial : null,
      anexoDecisaoLiminar:
        anexoDecisaoLiminar && liminarFamiliaSubstituta === "S"
          ? anexoDecisaoLiminar
          : null,
      anexoSentenca: anexoSentenca && sentenca === "S" ? anexoSentenca : null,
      anexoPecaRecursal:
        anexoPecaRecursal && recurso === "S" ? anexoPecaRecursal : null,
      anexoCertidaoTransitoJulgado:
        anexoCertidaoTransitoJulgado && transitoJulgado === "S"
          ? anexoCertidaoTransitoJulgado
          : null,
      informacaoComplementar: informacaoComplementar
        ? informacaoComplementar.trim()
        : "",
      compartilharProcesso:
        irmaos && irmaos.length > 0 && compartilharProcesso === "S"
          ? "S"
          : compartilharProcesso === "N"
          ? "N"
          : "",
      irmaos: compartilharProcesso === "S" ? irmaos : [],
      idSituacaoJuridica: idSituacaoJuridica ? idSituacaoJuridica.id : "",
      idTipoSentenca:
        idTipoSentenca && sentenca === "S" ? idTipoSentenca.id : "",
      idAnexoDecisaoLiminar: selectedFiliacao?.anexoDecisaoLiminar?.id
        ? selectedFiliacao.anexoDecisaoLiminar.id
        : 0,
      atualizarAnexoDecisaoLiminar: true,
      removerAnexoDecisaoLiminar:
        (anexoDecisaoLiminarEditado && !anexoDecisaoLiminar) ||
        (anexoDecisaoLiminar && liminarFamiliaSubstituta !== "S")
          ? true
          : false,
      idAnexoSentenca: selectedFiliacao?.anexoSentenca?.id
        ? selectedFiliacao.anexoSentenca.id
        : 0,
      atualizarAnexoSentenca: true,
      removerAnexoSentenca:
        (anexoSentencaEditado && !anexoSentenca) ||
        (anexoSentenca && sentenca !== "S")
          ? true
          : false,
      idAnexoPecaRecursal: selectedFiliacao?.anexoPecaRecursal?.id
        ? selectedFiliacao.anexoPecaRecursal.id
        : 0,
      atualizarAnexoPecaRecursal: true,
      removerAnexoPecaRecursal:
        (anexoPecaRecursalEditado && !anexoPecaRecursal) ||
        (anexoPecaRecursal && recurso !== "S")
          ? true
          : false,
      idAnexoCertidaoTransitoJulgado: selectedFiliacao
        ?.anexoCertidaoTransitoJulgado?.id
        ? selectedFiliacao.anexoCertidaoTransitoJulgado.id
        : 0,
      atualizarAnexoCertidaoTransitoJulgado: true,
      removerAnexoCertidaoTransitoJulgado:
        (anexoCertidaoTransitoJulgadoEditado &&
          !anexoCertidaoTransitoJulgado) ||
        (anexoCertidaoTransitoJulgado && transitoJulgado !== "S")
          ? true
          : false,
      idAnexoPeticaoInicial: selectedFiliacao?.anexoPeticaoInicial?.id
        ? selectedFiliacao.anexoPeticaoInicial.id
        : 0,
      atualizarAnexoPeticaoInicial: true,
      removerAnexoPeticaoInicial:
        anexoPeticaoInicialEditado && !anexoPeticaoInicial ? true : false,
    };

    editProcessoProcedimento(idCrianca, valuesToSend, selectedFiliacao?.id);
  };

  const endOfModalRef = useRef(null);

  const handleScrollToBottom = useCallback(() => {
    if (endOfModalRef.current) {
      endOfModalRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        scrollBehavior={"inside"}
      >
        <ModalOverlay />
        <ModalContent
          p={{ base: "5px", sm: "15px", md: "25px" }}
          w={{ base: "100%", lg: "50%" }}
          bgColor="#FFF"
        >
          {isVisual ? (
            <SituacaoJuridicaVisualizarModal
              isOpen={isOpen}
              onClose={onClose}
              selectedFiliacao={selectedFiliacao}
              setIsVisual={setIsVisual}
              irmaosData={irmaosData}
            />
          ) : (
            <>
              <ModalHeader
                p="0px"
                fontWeight={700}
                fontSize="14px"
                lineHeight="19.6px"
                color="#169499"
              >
                {"Editar Processo/Procedimento".toUpperCase()}
              </ModalHeader>
              <ModalBody
                p="0px"
                mt="25px"
                minH={{ base: "0px", md: "200px" }}
                pl="1px"
              >
                <Formik
                  validationSchema={schema}
                  initialValues={initialValues}
                  onSubmit={handleSubmit}
                  validateOnChange={false}
                  validateOnBlur={false}
                >
                  {(props) => (
                    <Form style={{ width: "100%" }}>
                      {setCadastrarProcessosProcedimentos(
                        hasFormChanged(initialValues, props.values)
                      )}
                      <FocusError />
                      <Grid templateColumns="repeat(6, 1fr) 1fr" gap={2}>
                        <GridItem
                          display="flex"
                          border="1px solid #b3b3b3"
                          rounded="xl"
                          flexDirection={{ base: "column", md: "row" }}
                          px={2}
                          py={3}
                          gap={4}
                          colSpan={7}
                        >
                          <Grid templateColumns="repeat(8, 1fr)" gap={4}>
                            <GridItem colSpan={{ base: 8, md: 4 }}>
                              <CustomInputWField
                                fieldName="numeroProcesso"
                                label="Nº do Processo/Procedimento*"
                                placeholder="Informe o Número"
                                onChange={(e) =>
                                  props.setFieldValue(
                                    "numeroProcesso",
                                    numeroProcesso(e.target.value)
                                  )
                                }
                              />
                            </GridItem>
                            <GridItem colSpan={{ base: 8, md: 4 }}>
                              <CustomSelectWField
                                fieldName="idSituacaoJuridica"
                                label="Tipo*"
                                placeholder="Selecione o Tipo"
                                getOptionLabel={(option) => option?.label}
                                getOptionValue={(option) => option.id}
                                isLoading={servicosData.isLoading}
                                options={servicosData.options}
                                onChange={(value) => {
                                  props.setFieldValue(
                                    "idSituacaoJuridica",
                                    value
                                  );
                                }}
                                onFocus={() => handleGetServicos()}
                              />
                            </GridItem>
                            <GridItem colSpan={{ base: 8, md: 2 }}>
                              <Flex>
                                <CustomDropzone
                                  formProps={props}
                                  title={`Anexar Petição Inicial`}
                                  fieldName="anexoPeticaoInicial"
                                  height="42px"
                                  titleText="Anexar Petição Inicial"
                                  setAnexoNaoEditado={() => {
                                    setAnexoPeticaoInicialEditado(true);
                                  }}
                                />
                              </Flex>
                            </GridItem>
                          </Grid>
                        </GridItem>
                        <GridItem colSpan={7}>
                          <ChangeRadio
                            setFieldValue={props.setFieldValue}
                            values={props.values}
                            fieldName={"liminarFamiliaSubstituta"}
                            fieldNameAnexo={"anexoDecisaoLiminar"}
                            label={
                              "Há Decisão Liminar de Colocação em Família Substituta?*"
                            }
                            labelAnexo={"Anexar Decisão Liminar"}
                            setAnexo={setAnexoDecisaoLiminarEditado}
                          />
                        </GridItem>
                        <GridItem colSpan={7}>
                          <ChangeRadio
                            setFieldValue={props.setFieldValue}
                            values={props.values}
                            fieldName={"sentenca"}
                            fieldNameAnexo={"anexoSentenca"}
                            label={"Sentença?*"}
                            labelAnexo={"Anexar Sentença"}
                            setAnexo={setAnexoSentencaEditado}
                          />
                        </GridItem>
                        <GridItem colSpan={7}>
                          <ChangeRadio
                            setFieldValue={props.setFieldValue}
                            values={props.values}
                            fieldName={"recurso"}
                            fieldNameAnexo={"anexoPecaRecursal"}
                            label={"Recurso?*"}
                            labelAnexo={"Anexar Peça Recursal"}
                            setAnexo={setAnexoPecaRecursalEditado}
                          />
                        </GridItem>
                        <GridItem colSpan={7}>
                          <ChangeRadio
                            setFieldValue={props.setFieldValue}
                            values={props.values}
                            fieldName={"transitoJulgado"}
                            fieldNameAnexo={"anexoCertidaoTransitoJulgado"}
                            label={"Trânsito em Julgado?*"}
                            labelAnexo={
                              "Anexar Certidão de Trânsito em Julgado"
                            }
                            setAnexo={setAnexoCertidaoTransitoJulgadoEditado}
                          />
                        </GridItem>
                        <GridItem
                          colSpan={7}
                          border="1px solid #b3b3b3"
                          rounded="xl"
                          px={2}
                          py={3}
                        >
                          <CustomTextArea
                            fieldName={"informacaoComplementar"}
                            label={"Informações Complementares"}
                            placeholder={
                              "Informe as Informações Complementares"
                            }
                            setIsTextareaFocused={setIsTextareaFocused}
                            maxLength={255}
                            isTextareaFocused={isTextareaFocused}
                            fontWeightLabel={500}
                            colorLabel="#5F6377"
                            fontSizeLabel={"14px"}
                            lineHeightLabel={"19.6px"}
                          />
                        </GridItem>

                        {irmaosData && irmaosData.options.length > 0 && (
                          <GridItem colSpan={7} position={"relative"}>
                            <ChangeRadio
                              setFieldValue={props.setFieldValue}
                              values={props.values}
                              fieldName={"compartilharProcesso"}
                              fieldNameCheck={"irmaos"}
                              idCrianca={idCrianca}
                              label={
                                <Flex>
                                  <Text textTransform="uppercase">
                                    Deseja Compartilhar Processo/Procedimento na
                                    Ficha do Irmão?
                                  </Text>
                                  {isMobile ? (
                                    <PopoverGenero
                                      isOpen={hintCompartilhar}
                                      setIsOpen={setHintCompartilhar}
                                      flex={true}
                                      handleClick={() =>
                                        setHintCompartilhar(!hintCompartilhar)
                                      }
                                      labelTextoGenero={
                                        "Ao compartilhar um processo/procedimento com irmão(s), todas as informações serão importadas para ficha do(s) irmão(s) e as atualizações serão feitas de forma simultânea, ou seja, ao acessar qualquer uma das fichas e atualizar as informações sobre um processo/procedimento compartilhado, automaticamente essas informações também serão atualizadas na(s) ficha(s) do(s) irmão(s). Caso o processo/procedimento seja descompartilhado, as informações serão excluídas somente da ficha descompartilhada."
                                      }
                                    />
                                  ) : (
                                    <Tooltip
                                      label={
                                        "Ao compartilhar um processo/procedimento com irmão(s), todas as informações serão importadas para ficha do(s) irmão(s) e as atualizações serão feitas de forma simultânea, ou seja, ao acessar qualquer uma das fichas e atualizar as informações sobre um processo/procedimento compartilhado, automaticamente essas informações também serão atualizadas na(s) ficha(s) do(s) irmão(s). Caso o processo/procedimento seja descompartilhado, as informações serão excluídas somente da ficha descompartilhada."
                                      }
                                      fontSize="md"
                                      maxW="400px"
                                      textAlign={"justify"}
                                    >
                                      <span className="hint-tooltip">
                                        <Icon
                                          aria-label="Aviso de pendente"
                                          color="#6B6C71"
                                          as={IoHelpCircle}
                                          boxSize={{ base: "20px" }}
                                        />
                                      </span>
                                    </Tooltip>
                                  )}
                                </Flex>
                              }
                              handleScrollToBottom={handleScrollToBottom}
                              editar={true}
                            />
                            {irmaosData &&
                              roleKeycloack &&
                              roleKeycloack.includes(
                                servicoAcolhimentoFunc.visualizarParteTJ
                              ) && (
                                <Box
                                  position="absolute"
                                  top={0}
                                  left={0}
                                  right={0}
                                  bottom={0}
                                  opacity={0.3}
                                  bg="gray"
                                  zIndex={1}
                                  cursor="not-allowed"
                                  borderRadius="md"
                                />
                              )}
                          </GridItem>
                        )}
                      </Grid>
                      <Flex justifyContent="end">
                        <SimpleGrid
                          mt="25px"
                          columns={2}
                          spacing="15px"
                          w={{ base: "100%", lg: "40%" }}
                        >
                          <CustomButton
                            type={"button"}
                            onClick={backAlert}
                            label="Cancelar"
                          />
                          <CustomButton
                            type="submit"
                            translate="no"
                            isDisabled={loading}
                            isLoading={loading}
                            label="Salvar"
                          />
                        </SimpleGrid>
                      </Flex>
                      <Box ref={endOfModalRef} />
                    </Form>
                  )}
                </Formik>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
